import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Input, ConfirmationDialog } from "../../../../components/ui";
import { CustomAssetReferenceEditor } from "../../../../components/rich-text-editor";
import { DmNotesField } from "../../../../components/business";
import {
  CampaignAssetService,
  CreateCampaignAssetSectionRequest,
  UpdateCampaignAssetSectionRequest,
} from "../../../../services/campaignAssetService";
import { CharacterService } from "../../../../services/characterService";
import {
  CampaignAsset,
  CharacterListItem,
  CampaignAssetAccessRequest,
  CampaignAssetDiceTemplate,
} from "../../../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faUsers,
  faTrash,
  faSave,
  faArrowLeft,
  faPlus,
  faEdit,
  faArrowUp,
  faArrowDown,
  faExternalLinkAlt,
  faTimes,
  faDice,
} from "@fortawesome/free-solid-svg-icons";

const CampaignAssetEditPage: React.FC = () => {
  const { campaignId, assetId } = useParams<{ campaignId: string; assetId: string }>();
  const navigate = useNavigate();

  const [campaignAsset, setCampaignAsset] = useState<CampaignAsset | null>(null);
  const [allCampaignAssets, setAllCampaignAssets] = useState<CampaignAsset[]>([]);
  const [characters, setCharacters] = useState<CharacterListItem[]>([]);
  const [selectedCharacters, setSelectedCharacters] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState<string>("");

  // Section editing state
  const [editingSections, setEditingSections] = useState<{ [key: number]: boolean }>({});
  const [newSection, setNewSection] = useState({ name: "", content: "" });
  const [showNewSection, setShowNewSection] = useState(false);

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    variant?: "danger" | "warning" | "info";
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [sectionForms, setSectionForms] = useState<{
    [key: number]: { name: string; content: string; dmNotes: string };
  }>({});

  // Fate Formulas — Beast and NPC entries only (GM-private, read-only in chronicle view)
  const [diceTemplates, setDiceTemplates] = useState<CampaignAssetDiceTemplate[]>([]);

  const supportsFateFormulas = (type?: string) =>
    type === "Character" || type === "Beast" || type === "NPC";

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, assetId]);

  const loadData = async () => {
    if (!campaignId || !assetId) return;

    try {
      setLoading(true);
      setError(null);

      const [assetData, assetsData, charactersData] = await Promise.all([
        CampaignAssetService.getCampaignAsset(parseInt(campaignId), parseInt(assetId)),
        CampaignAssetService.getSharedCampaignAssets(parseInt(campaignId)),
        CharacterService.getCharacterList({
          name: "",
          campaignId: campaignId,
          page: 0,
          pageSize: 100,
        }),
      ]);

      setCampaignAsset(assetData);
      setAllCampaignAssets(assetsData);
      setCharacters(charactersData.characters); // Extract characters from response
      setSelectedCharacters(assetData.accessUsers?.map((access) => access.characterId) || []);
      setEditedDescription(assetData.asset?.description ?? "");

      // Load Fate Formulas for Beast/NPC entries
      if (supportsFateFormulas(assetData.asset?.type)) {
        try {
          const templates = await CampaignAssetService.getCampaignAssetDiceTemplates(
            parseInt(campaignId),
            parseInt(assetId)
          );
          setDiceTemplates(templates);
        } catch {
          // Silently ignore — user may not be the creator
          setDiceTemplates([]);
        }
      }

      // Initialize section forms for campaign asset sections
      const sectionFormsData: {
        [key: number]: { name: string; content: string; dmNotes: string };
      } = {};
      assetData.sections?.forEach((section) => {
        sectionFormsData[section.id] = {
          name: section.name,
          content: section.content,
          dmNotes: section.dmNotes ?? "",
        };
      });
      setSectionForms(sectionFormsData);
    } catch (error) {
      console.error("Failed to load campaign asset:", error);
      setError("Failed to load campaign asset. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!campaignAsset || !campaignId) return;

    try {
      setSaving(true);
      setError(null);

      // Save description if it changed
      const descriptionChanged = editedDescription !== (campaignAsset.asset?.description ?? "");
      if (descriptionChanged) {
        await CampaignAssetService.updateCampaignAsset(parseInt(campaignId), campaignAsset.id, {
          description: editedDescription,
        });
      }

      if (campaignAsset.sections.length > 0) {
        // Update sections that have changed
        for (const section of campaignAsset.sections) {
          const sectionForm = sectionForms[section.id];
          if (
            sectionForm &&
            (sectionForm.name !== section.name || sectionForm.content !== section.content)
          ) {
            const updateRequest: UpdateCampaignAssetSectionRequest = {
              name: sectionForm.name,
              content: sectionForm.content,
              dmNotes: sectionForm.dmNotes,
            };
            await CampaignAssetService.updateCampaignAssetSection(
              parseInt(campaignId),
              campaignAsset.id,
              section.id,
              updateRequest
            );
          }
        }
      }

      // Update character access
      const currentCharacterIds =
        campaignAsset.accessUsers?.map((access) => access.characterId) || [];
      const toAdd = selectedCharacters.filter((id) => !currentCharacterIds.includes(id));
      const toRemove = currentCharacterIds.filter((id) => !selectedCharacters.includes(id));

      if (toAdd.length > 0) {
        const addRequest: CampaignAssetAccessRequest = {
          assetAccess: toAdd.map((id) => ({ characterId: id })),
        };
        await CampaignAssetService.grantAssetAccess(
          parseInt(campaignId),
          campaignAsset.id,
          addRequest
        );
      }

      if (toRemove.length > 0) {
        const removeRequest: CampaignAssetAccessRequest = {
          assetAccess: toRemove.map((id) => ({ characterId: id })),
        };
        await CampaignAssetService.revokeAssetAccess(
          parseInt(campaignId),
          campaignAsset.id,
          removeRequest
        );
      }

      // Exit editing mode and reload data to reflect changes
      setIsEditing(false);
      await loadData(); // Reload the campaign asset data
    } catch (error) {
      console.error("Failed to save campaign asset:", error);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (campaignAsset) {
      setSelectedCharacters(campaignAsset.accessUsers?.map((access) => access.characterId) || []);
      setEditedDescription(campaignAsset.asset?.description ?? "");

      // Reset section forms
      const sectionFormsData: {
        [key: number]: { name: string; content: string; dmNotes: string };
      } = {};
      campaignAsset.sections.forEach((section) => {
        sectionFormsData[section.id] = {
          name: section.name,
          content: section.content,
          dmNotes: section.dmNotes ?? "",
        };
      });
      setSectionForms(sectionFormsData);

      // Reset section editing states
      setEditingSections({});
      setNewSection({ name: "", content: "" });
      setShowNewSection(false);
    }

    // Exit editing mode
    setIsEditing(false);
    setError(null);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSaveAssetDmNotes = async (dmNotes: string) => {
    if (!campaignAsset || !campaignId) return;
    await CampaignAssetService.updateCampaignAsset(parseInt(campaignId), campaignAsset.id, {
      dmNotes,
    });
    setCampaignAsset((prev) => (prev ? { ...prev, dmNotes } : prev));
  };

  const handleSaveSectionDmNotes = async (sectionId: number, dmNotes: string) => {
    if (!campaignAsset || !campaignId) return;
    const section = campaignAsset.sections.find((s) => s.id === sectionId);
    if (!section) return;
    await CampaignAssetService.updateCampaignAssetSection(
      parseInt(campaignId),
      campaignAsset.id,
      sectionId,
      { name: section.name, content: section.content, dmNotes }
    );
    // Keep campaignAsset and sectionForms in sync so a subsequent full save
    // does not overwrite freshly saved DM Notes with the stale form value.
    setCampaignAsset((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, dmNotes } : s)),
      };
    });
    setSectionForms((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], dmNotes },
    }));
  };

  const handleCreateSection = async () => {
    if (!campaignAsset || newSection.name.trim() === "") return;

    try {
      const createRequest: CreateCampaignAssetSectionRequest = {
        name: newSection.name.trim(),
        content: newSection.content.trim(),
        sectionOrder: 0,
      };

      await CampaignAssetService.createCampaignAssetSection(
        parseInt(campaignId!),
        campaignAsset.id,
        createRequest
      );

      // Reset form and reload data
      setNewSection({ name: "", content: "" });
      setShowNewSection(false);
      await loadData();
    } catch (error) {
      console.error("Failed to create folio:", error);
      setError("Failed to create folio. Please try again.");
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!campaignAsset) return;

    setConfirmDialog({
      isOpen: true,
      title: "Delete Folio",
      message: "Are you sure you want to delete this section? This action cannot be undone.",
      variant: "danger",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        try {
          await CampaignAssetService.deleteCampaignAssetSection(
            parseInt(campaignId!),
            campaignAsset.id,
            sectionId
          );
          await loadData(); // Reload to refresh sections
        } catch (error) {
          console.error("Failed to delete folio:", error);
          setError("Failed to delete folio. Please try again.");
        }
      },
      onCancel: () => setConfirmDialog((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const toggleSectionEdit = (sectionId: number) => {
    setEditingSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const updateSectionForm = (sectionId: number, field: "name" | "content", value: string) => {
    setSectionForms((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value,
      },
    }));
  };

  const handleDeleteAsset = async () => {
    if (!campaignAsset || !campaignId) return;

    setConfirmDialog({
      isOpen: true,
      title: "Remove Asset from Campaign",
      message:
        "Are you sure you want to remove this asset from the campaign? This action cannot be undone.",
      variant: "danger",
      confirmText: "Remove",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        try {
          setSaving(true);
          setError(null);

          await CampaignAssetService.removeAssetFromCampaign(
            parseInt(campaignId),
            campaignAsset.id
          );

          navigate(`/protected/campaigns/${campaignId}`);
        } catch (error) {
          console.error("Failed to delete chronicle asset:", error);
          setError("Failed to remove entry from chronicle. Please try again.");
        } finally {
          setSaving(false);
        }
      },
      onCancel: () => setConfirmDialog((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const toggleCharacterSelection = (characterId: number) => {
    setSelectedCharacters((prev) =>
      prev.includes(characterId) ? prev.filter((id) => id !== characterId) : [...prev, characterId]
    );
  };

  // Helper functions for asset navigation
  const findParentCampaignAsset = (): CampaignAsset | null => {
    if (!campaignAsset?.asset.parentId) return null;
    return allCampaignAssets.find((ca) => ca.asset.id === campaignAsset.asset.parentId) || null;
  };

  const findChildrenCampaignAssets = (): CampaignAsset[] => {
    if (!campaignAsset) return [];
    return allCampaignAssets.filter((ca) => ca.asset.parentId === campaignAsset.asset.id);
  };

  const navigateToAsset = (targetAssetId: number) => {
    const targetCampaignAsset = allCampaignAssets.find((ca) => ca.asset.id === targetAssetId);
    if (targetCampaignAsset) {
      navigate(`/protected/campaigns/${campaignId}/assets/${targetCampaignAsset.id}/edit`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <div className="flex justify-center items-center py-8">
          <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-yellow-400" />
          <span className="ml-2 text-white">Loading campaign asset...</span>
        </div>
      </div>
    );
  }

  if (error || !campaignAsset) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <div className="text-center py-8">
          <p className="text-red-400 mb-4">{error || "Campaign asset not found"}</p>
          <Button
            variant="primary"
            onClick={() => navigate(`/protected/campaigns/${campaignId}`)}
            className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400"
          >
            Back to Campaign
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/protected/campaigns/${campaignId}`)}
              className="text-yellow-400 hover:text-yellow-300 p-2"
              title="Back to Campaign"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Edit Campaign Asset</h1>
              <p className="text-gray-400 text-sm">{campaignAsset.asset.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={handleDeleteAsset}
              disabled={saving || isEditing}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faTrash} />
              Remove from Campaign
            </Button>

            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={saving}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faSave} />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={handleStartEdit}
                disabled={saving}
                className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Asset Information (Read-only) */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Asset Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
              <p className="text-white bg-gray-700 px-3 py-2 rounded border">
                {campaignAsset.asset.name}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
              <p className="text-white bg-gray-700 px-3 py-2 rounded border">
                {campaignAsset.asset.type}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
              <p className="text-white bg-gray-700 px-3 py-2 rounded border">
                {campaignAsset.asset.category}
              </p>
            </div>
            {(campaignAsset.asset.description || isEditing) && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <p>
                  <CustomAssetReferenceEditor
                    value={isEditing ? editedDescription : campaignAsset.asset.description}
                    onChange={isEditing ? setEditedDescription : undefined}
                    readOnly={!isEditing}
                    navigationUrlFormat="/protected/campaigns/:campaignId/assets/:id/edit"
                    campaignId={parseInt(campaignId!)}
                  />
                </p>
              </div>
            )}
            <div className="md:col-span-2 mt-2">
              <DmNotesField value={campaignAsset.dmNotes ?? ""} onSave={handleSaveAssetDmNotes} />
            </div>
          </div>

          {/* Asset Hierarchy Navigation */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <h3 className="text-lg font-medium text-white mb-3">Asset Hierarchy</h3>
            <div className="flex flex-col gap-3">
              {/* Parent Asset */}
              {(() => {
                const parentAsset = findParentCampaignAsset();
                return parentAsset ? (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faArrowUp} className="text-blue-400" />
                    <span className="text-gray-300 text-sm">Parent:</span>
                    <Button
                      variant="secondary"
                      onClick={() => navigateToAsset(parentAsset.asset.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                      {parentAsset.asset.name}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faArrowUp} className="text-gray-500" />
                    <span className="text-gray-500 text-sm">No parent asset</span>
                  </div>
                );
              })()}

              {/* Children Assets */}
              {(() => {
                const childrenAssets = findChildrenCampaignAssets();
                return childrenAssets.length > 0 ? (
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faArrowDown} className="text-green-400 mt-1" />
                    <span className="text-gray-300 text-sm">Children:</span>
                    <div className="flex flex-wrap gap-2">
                      {childrenAssets.map((childAsset) => (
                        <Button
                          key={childAsset.id}
                          variant="secondary"
                          onClick={() => navigateToAsset(childAsset.asset.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                          {childAsset.asset.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faArrowDown} className="text-gray-500" />
                    <span className="text-gray-500 text-sm">No child assets</span>
                  </div>
                );
              })()}
            </div>
          </div>

          <p className="text-gray-400 text-sm mt-4">
            Note: To edit the asset information, go to the main asset management page.
          </p>
        </div>

        {/* Asset Sections */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Entry Folios</h2>
            <div className="flex items-center gap-3">
              {!isEditing && (
                <span className="text-yellow-400 text-sm">Click "Edit" to modify sections</span>
              )}
              {isEditing && (
                <Button
                  variant="primary"
                  onClick={() => setShowNewSection(!showNewSection)}
                  className="bg-yellow-500 text-black px-3 py-1 rounded hover:bg-yellow-400 flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Section
                </Button>
              )}
            </div>
          </div>

          {/* New Section Form */}
          {showNewSection && isEditing && (
            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-medium text-white mb-3">New Folio</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Input
                    label="Name"
                    type="text"
                    value={newSection.name}
                    onChange={(e) => setNewSection((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Folio name"
                    fullWidth
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
                  <CustomAssetReferenceEditor
                    value={newSection.content}
                    onChange={(content) => setNewSection((prev) => ({ ...prev, content }))}
                    placeholder="Folio content. Use [[ to reference campaign assets."
                    campaignId={parseInt(campaignId!)}
                    navigationUrlFormat="/protected/campaigns/:campaignId/assets/:id/edit"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleCreateSection}
                    disabled={!newSection.name.trim()}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Folio
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowNewSection(false);
                      setNewSection({ name: "", content: "" });
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Sections */}
          {campaignAsset.sections?.length === 0 ? (
            <p className="text-gray-400 text-center py-4">
              No sections available. Add a section to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {campaignAsset.sections?.map((section) => (
                <div key={section.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-medium text-white">
                      {isEditing && editingSections[section.id] ? (
                        <Input
                          type="text"
                          value={sectionForms[section.id]?.name || section.name}
                          onChange={(e) => updateSectionForm(section.id, "name", e.target.value)}
                        />
                      ) : (
                        section.name
                      )}
                    </h3>
                    {isEditing && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => toggleSectionEdit(section.id)}
                          className="text-yellow-400 hover:text-yellow-300 p-1"
                          title={editingSections[section.id] ? "Stop Editing" : "Edit Folio"}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDeleteSection(section.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Delete Folio"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    {isEditing && editingSections[section.id] ? (
                      <CustomAssetReferenceEditor
                        value={sectionForms[section.id]?.content || section.content}
                        onChange={(content) => updateSectionForm(section.id, "content", content)}
                        placeholder="Folio content. Use >> to reference campaign assets."
                        campaignId={parseInt(campaignId!)}
                        navigationUrlFormat="/protected/campaigns/:campaignId/assets/:id/edit"
                      />
                    ) : (
                      <CustomAssetReferenceEditor
                        value={section.content}
                        readOnly={true}
                        campaignId={parseInt(campaignId!)}
                        navigationUrlFormat="/protected/campaigns/:campaignId/assets/:id/edit"
                      />
                    )}
                  </div>
                  <div className="mt-3">
                    <DmNotesField
                      value={section.dmNotes ?? ""}
                      onSave={(dmNotes) => handleSaveSectionDmNotes(section.id, dmNotes)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Character Access */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} />
            Character Access
          </h2>
          <p className="text-gray-300 text-sm mb-4">
            Select which characters can access this asset in the campaign.
            {!isEditing && (
              <span className="block text-yellow-400 text-xs mt-1">
                Click "Edit" to modify character access
              </span>
            )}
          </p>

          {characters.length === 0 ? (
            <p className="text-gray-400 text-center py-4">
              No characters available in this campaign
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {characters.map((character) => (
                <label
                  key={character.id}
                  className={`flex items-center p-3 bg-gray-700 rounded-lg transition-colors ${
                    isEditing ? "hover:bg-gray-600 cursor-pointer" : "cursor-default"
                  }`}
                >
                  <Input
                    type="checkbox"
                    checked={selectedCharacters?.includes(character.id)}
                    onChange={() => toggleCharacterSelection(character.id)}
                    disabled={!isEditing}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium">{character.name}</span>
                    <p className="text-gray-400 text-sm">{character.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fate Formulas — Beast & NPC Chronicle Entries (GM-private, not shareable) */}
      {supportsFateFormulas(campaignAsset?.asset?.type) && diceTemplates.length > 0 && (
        <div className="bg-gray-800 border border-yellow-700/40 rounded-lg p-6 mb-6 mx-6">
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faDice} className="text-yellow-400" />
            <h2 className="text-xl font-semibold text-white">Fate Formulas</h2>
            <span className="ml-2 text-xs text-yellow-500 bg-yellow-900/30 border border-yellow-700/40 px-2 py-0.5 rounded-full">
              GM only · not shared
            </span>
          </div>
          <div className="space-y-2">
            {diceTemplates.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between bg-gray-700 rounded-lg px-4 py-2 text-sm"
              >
                <div className="flex-1">
                  <span className="text-white font-medium">{t.name}</span>
                  {t.description && (
                    <span className="ml-2 text-gray-400 text-xs">{t.description}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <span className="font-mono text-yellow-300">{t.diceNotation}</span>
                  {t.modifier !== 0 && (
                    <span className="text-gray-400">
                      {t.modifier > 0 ? `+${t.modifier}` : t.modifier}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">{t.skillType}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

export default CampaignAssetEditPage;
